import React, { useEffect, useState } from 'react';
import Chart from 'react-apexcharts';
import { getFormattedDate, getCompactDollars, getUngroupedMax1Decimals } from '../../helpers/dataFormatters';
import { chartModes, getOverviewGraphData } from '../../helpers/agentDataCalculators';
import { agentProductionTerms } from '../../../constants';
import { OverviewGraphLabelMaker } from '../../helpers/uiHelpers';
import { setStateData } from '../../../common/helpers/state';
import { dataPointMouseOverStyler } from '../../helpers/schemaTableHelpers';
import { usePDFStore } from '../../../store/pdf/store';
import { agentProfileTabs } from '../../constants/agentProductionConstants';
import PropTypes from 'prop-types';
import { COLOR_PRIMARY_GREEN, COLOR_SECONDARY_GREEN } from '../../../common/constants/colors';

export const OverviewChartGraph = props => {

    const {
        chartData, dataIndexes, dataPointFormatter, yAxisTitle, yAxisFormatter, graphMode, isShowColumnLabels,
        listMapper, sellMapper, countYAxisTicks, handleClickChartGraph, currentTabIndex,
    } = props;

    const isPDFLoading = usePDFStore((state) => state.downloadModalStatus.isPDFLoading);

    const CONTAINER_WIDTH_MAX = '100%';
    const MINIMUM_COLUMN_WIDTH_IN_PIXELS = 85;

    const [state, setState] = useState({
        windowWidth: window.innerWidth,
        widthGraph: CONTAINER_WIDTH_MAX,
        styleContainer: {}
    });

    const graphData = getOverviewGraphData({ chartData, dataIndexes, listMapper, sellMapper });
    const countColumns = graphData.xAxisLabels.length;
    const widthGraphContent = countColumns * MINIMUM_COLUMN_WIDTH_IN_PIXELS;

    useEffect(() => {
        const handleResize = e => setStateData('windowWidth', e.target.innerWidth, setState);
        window.addEventListener('resize', handleResize, true);
        return () => window.removeEventListener('resize', handleResize, true);
    }, []);

    useEffect(() => {
        if (widthGraphContent <= window.innerWidth) {
            setState(prevState => ({
                ...prevState,
                widthGraph: CONTAINER_WIDTH_MAX,
                styleContainer: {
                    maxWidth: CONTAINER_WIDTH_MAX,
                }
            }));
        } else {
            setState(prevState => ({
                ...prevState,
                widthGraph: widthGraphContent.toString(),
                styleContainer: {
                    maxWidth: 'none'
                }
            }));
        }
    }, [widthGraphContent]);

    const dataPointGroups = [
        { name: agentProductionTerms.listSideSold, data: graphData.listSideSoldValues },
        { name: agentProductionTerms.sellSideSold, data: graphData.sellSideSoldValues }
    ];

    const colors = [COLOR_PRIMARY_GREEN, COLOR_SECONDARY_GREEN];
    const fontFamily = '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,' +
        'sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol"';

    const getToolTipHeader = (dataPointIndex, graphInfo) => {

        const labelColumnXAxis = graphInfo.globals.labels[dataPointIndex];
        const dateColumn = OverviewGraphLabelMaker.getDateMonthFromGraphLabel(labelColumnXAxis);
        return getFormattedDate(dateColumn, 'MMMM yyyy');
    };

    const getTooltipContent = (series, dataPointIndex, graphInfo) => {

        const amountList = series[0][dataPointIndex];
        const amountSell = series[1][dataPointIndex];
        const tooltipHeader = getToolTipHeader(dataPointIndex, graphInfo);

        return `<div class='arrow_box volume-chart-tooltip'>
		<div class='header'> 
			${tooltipHeader}
		</div> 
		<div class='body'>
			<div class='items'>
				<span class='bullet' style='background-color: ${COLOR_SECONDARY_GREEN}'></span> 
					${dataPointFormatter(amountSell)} <br />
				<span class='bullet' style='background-color: ${COLOR_PRIMARY_GREEN}'></span> 
					${dataPointFormatter(amountList)}
			</div>
		 	<div class='total'>Total: ${dataPointFormatter(amountSell + amountList)}</div>
			</div>
		</div>`;
    };

    const getColumnLabel = (graphMode, dataPointIndex) => {

        let value, formatter;

        if (graphMode === chartModes.UNITS) {
            value = graphData?.formattedData[dataPointIndex].totalUnits;
            formatter = getUngroupedMax1Decimals;
        } else {
            value = graphData?.formattedData[dataPointIndex].totalVolume;
            formatter = getCompactDollars;
        }

        if (value === 0) return '';

        return formatter(value);
    };

    const getChartOptions = (isPDF) => ({
        chart: {
            animations: {
                enabled: false
            },
            type: 'bar',
            stacked: true,
            toolbar: {
                show: false
            },
            fontFamily,
            noData: {
                text: agentProductionTerms.noDataForSelectedAgent
            },
            zoom: {
                enabled: false
            },
            events: {
                dataPointSelection: (event, chartContext, config) => {
                    const selectedChartIndex = config.dataPointIndex;
                    handleClickChartGraph(config.w.globals.labels[selectedChartIndex]);
                },
                dataPointMouseEnter: dataPointMouseOverStyler
            }
        },
        responsive: [{
            breakpoint: 480,
            options: {
                legend: {
                    position: 'left',
                    offsetX: -10,
                    offsetY: 0
                }
            }
        }],
        plotOptions: {
            bar: {
                horizontal: false,
                borderRadius: 1,
                columnWidth: '40px',
                dataLabels: {
                    position: 'top',
                    hideOverflowingLabels: false
                }
            }
        },
        dataLabels: {
            enabled: isShowColumnLabels,
            enabledOnSeries: [1],
            textAnchor: 'middle',
            formatter: (_, { dataPointIndex }) => getColumnLabel(graphMode, dataPointIndex),
            style: {
                fontSize: isPDF ? '20px' : undefined,
                fontFamily: 'Helvetica, Arial, sans-serif',
                fontWeight: 'bold',
                colors: ['#212529']
            },
            offsetY: -30
        },
        xaxis: {
            type: 'category',
            categories: graphData.xAxisLabels,
            labels: {
                style: {
                    fontSize: isPDF ? '20px' : undefined,
                    colors: isPDF ? '#000' : '#6B7280',
                    capitalize: true
                }
            }
        },
        yaxis: {
            tickAmount: countYAxisTicks,
            labels: {
                formatter: yAxisFormatter,
                style: {
                    fontWeight: 400,
                    fontSize: isPDF ? '20px' : undefined,
                    colors: isPDF ? '#000' : '#6B7280',
                },
            },
            title: {
                text: yAxisTitle,
                style: {
                    fontSize: '24px',
                    fontWeight: 'normal'
                },
                offsetX: -5
            }
        },
        tooltip: {
            custom: ({ series, dataPointIndex, w: graphInfo }) => {
                return getTooltipContent(series, dataPointIndex, graphInfo);
            }
        },
        legend: {
            markers: {
                fillColors: colors,
                width: 15,
                height: 15,
                radius: 10
            },
            fontFamily,
            itemMargin: {
                horizontal: 10
            },
            onItemClick: {
                toggleDataSeries: false
            },
            position: 'top',
            offsetX: 0,
            horizontalAlign: 'left',
            fontSize: '16px',
        },
        fill: {
            opacity: 1,
            colors: colors
        }
    });

    const isShowEmptyState = chartData === undefined;
    const classContainer = state.widthGraph === CONTAINER_WIDTH_MAX ? '' : 'tableOverflow';
    const chartProps = {
        options: getChartOptions(),
        series: dataPointGroups,
        type: 'bar',
        height: 500,
        width: state.widthGraph
    };
    const pdfChartProps = { ...chartProps, options: getChartOptions(isPDFLoading)}

    return <div style={state.styleContainer} className={classContainer}>
        {isShowEmptyState
            ? <div className={'mt-3'}>
                {agentProductionTerms.searchReturnedNoResults}
            </div>
            : <>
                <div id="production-chart-graph">
                    <Chart {...chartProps} />
                </div>
                {isPDFLoading && currentTabIndex === agentProfileTabs.PRODUCTION && <div className="pdf-chart-agent-profile chart-capture-area">
                    <Chart {...pdfChartProps} />
                </div>}
            </>
        }
    </div>;
};

OverviewChartGraph.propTypes ={
    chartData: PropTypes.array,
    countYAxisTicks: PropTypes.number,
    currentTabIndex: PropTypes.number,
    dataIndexes: PropTypes.object,
    dataPointFormatter: PropTypes.func,
    graphMode: PropTypes.string,
    handleClickChartGraph: PropTypes.func,
    isShowColumnLabels: PropTypes.bool,
    listMapper: PropTypes.func,
    sellMapper: PropTypes.func,
    timePeriod: PropTypes.string,
    yAxisFormatter: PropTypes.func,
    yAxisTitle: PropTypes.string,
};
